import { plainToClass } from 'class-transformer';
import { inject, injectable } from 'inversify';
import { Observable, ReplaySubject, concat, firstValueFrom, from, of, throwError, timer } from 'rxjs';
import { catchError, finalize, first, map, mergeMap, retry, shareReplay, switchMap, tap } from 'rxjs/operators';

import { SettingsServiceConstants } from '../commons/commons';
import { Logger } from '../models/logger';
import { SessionDetails } from '../models/session-details';
import { BrowserUtils } from '../ui-utils/browser-utils';
import {
    AccountDetails,
    AuthenticateResponse,
    ErrorResponse,
    LoginResponse,
    ResponsePipe,
} from '../utils/request.utils';
import { ApiConfig } from './api-config';
import { AuthService } from './auth.service';
import { IdentityUnavailableError, LightHttp } from './light-http.service';
import { OfflineTaskService } from './offline-task.service';
import { ScriptLoadService } from './script-load.service';
import { StorageKeyPrefixeTypes, StorageService } from './storage.service';
import { UIContext } from './ui-context';
import { WindowService } from './window.service';


export enum SessionState {
    UNKNOWN = 'UNKNOWN',
    ANONYMOUS = 'ANONYMOUS',
    LOGGED_IN = 'LOGGED_IN',
}

async function sha256(message) {
    if (!message) {
        return message;
    }
    // encode as UTF-8
    const msgBuffer = new TextEncoder().encode(message);

    // hash the message
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

    // convert ArrayBuffer to Array
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    // convert bytes to hex string                  
    const hashHex = hashArray.map(b => ('00' + b.toString(16)).slice(-2)).join('');
    return hashHex;
}

@injectable()
export class LoginService {
    private readonly logger = Logger.getLogger('LoginService');

    public isUserLoggedIn = false;
    public canGoOffline = false;
    public isUserAuthenticated = false;
    private loginRequestDispatched = false;
    private inFlightLogin$: Observable<LoginResponse> | null = null;

    private _userDetails: AccountDetails = null;
    get userDetails() {
        return this._userDetails;
    }
    private readonly _onUserDetailsChanged = new ReplaySubject<AccountDetails>(1);
    get onUserDetailsChanged() {
        return this._onUserDetailsChanged.asObservable();
    }

    private _userIpAddress: string = null;
    get userIpAddress() {
        return this._userIpAddress;
    }

    private _userCountry: string = null;
    get userCountry() {
        return this._userCountry;
    }

    private _onSessionState = new ReplaySubject<SessionState>(1);
    private _currentSessionState: SessionState = SessionState.UNKNOWN;
    get onSessionState(): Observable<SessionState> {
        return this._onSessionState.asObservable();
    }
    get currentSessionState(): SessionState {
        return this._currentSessionState;
    }
    private emitSessionState(state: SessionState): void {
        if (this._currentSessionState === state) return;
        this._currentSessionState = state;
        this._onSessionState.next(state);
        if (state !== SessionState.UNKNOWN) this.identityEventsSubject?.next(state);
    }

    private _onSessionDetails = new ReplaySubject<SessionDetails>(1);
    get onSessionDetails(): Observable<SessionDetails> {
        return this._onSessionDetails.asObservable();
    }


    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(AuthService) private authService: AuthService,
        @inject(OfflineTaskService) private offlineTaskService: OfflineTaskService,
        @inject(StorageService) private storageService: StorageService,
        @inject(ScriptLoadService) private scriptLoadService: ScriptLoadService,
        @inject(UIContext) private uiContext: UIContext,
        @inject(WindowService) private windowService: WindowService,
        @inject(LightHttp) private lightHttp: LightHttp,
    ) {
        LightHttp.registerLoginUserDetailsProvider(this);
        // F3g: identity-bound calls wait for the identity this service establishes (see `identityWait`).
        LightHttp.registerIdentityGate({ wait: () => this.identityWait() });
    }

    /**
     * F3g (review 7): professorx rate-limits /auth/* at 30 requests / 60 s per IP. Behind a carrier or office NAT the
     * anonymous login can answer 429, and a browser with no identity then lost every identity-bound call to a silent 404
     * (bulk ZIP). With NO bearer held, the anonymous login waits out the 429 ONCE -- what the server's Retry-After says
     * (its bucket is a fixed 60 s, WEB-CORE-05), bounded at `ANON_429_MAX_WAIT_MS`, the toast saying how long -- and tries
     * again; identity-bound calls wait for it here, and fail with its error when it never came. With a bearer already held
     * (a registered session, an earlier anonymous one) a 429 on the re-check is the pre-F3g transient: no wait, no toast,
     * no fatal error (WEB-CORE-02).
     */
    private static readonly ANON_429_TRIES = 2;
    private static readonly ANON_429_MAX_WAIT_MS = 60_000;
    /** Set when the anonymous login gave up on 429; cleared by the next identity. */
    private identityFailure: IdentityUnavailableError | null = null;
    private identityRetryNoticed = false;

    /** For `LightHttp.identityGate`: null = nothing to wait for; else resolves when an identity is held, rejects when none will be. */
    private identityWait(): Promise<void> | null {
        if (this.authService.getToken()) return null;
        if (this.inFlightLogin$) {
            return firstValueFrom(this.inFlightLogin$).then(
                () => { if (!this.authService.getToken() && this.identityFailure) throw this.identityFailure; },
                // WEB-CORE-04: the login's OWN error (offline, 5xx...) -- "Too many sign-ins" only for the 429 give-up.
                (err) => { throw this.identityFailure ?? err; },
            );
        }
        if (this.identityFailure) return Promise.reject(this.identityFailure);
        // No identity has been asked for yet (a call made before the boot login is dispatched): wait for the first one --
        // an identity, or the failure -- bounded, so a page that never logs in is not held for ever.
        if (this._currentSessionState === SessionState.UNKNOWN && BrowserUtils.hasWindow()) {
            return new Promise<void>((resolve, reject) => {
                const done = () => { clearTimeout(timer); sub.unsubscribe(); };
                // WEB-CORE-03: the bound call is NEVER sent without an identity -- a wait that runs out fails it with the
                // identity error (sending it would be the silent 404 this gate exists to remove).
                const timer = setTimeout(() => {
                    done();
                    if (this.authService.getToken()) resolve();
                    else reject(new IdentityUnavailableError('We could not start your session. Please reload the page and try again.'));
                }, LoginService.IDENTITY_FIRST_WAIT_MS);
                const sub = this.identityEvents$().subscribe((e) => {
                    done();
                    if (typeof e === 'string') resolve(); else reject(e);
                });
            });
        }
        return null;
    }

    /** F3g: how long a call made before the first identity waits for it at most. */
    private static readonly IDENTITY_FIRST_WAIT_MS = 12_000;

    /** F3g: the identity's news -- a session state once one is held, or the failure. Created lazily. */
    private identityEventsSubject: ReplaySubject<SessionState | unknown> | null = null;
    private identityEvents$(): Observable<SessionState | unknown> {
        if (!this.identityEventsSubject) this.identityEventsSubject = new ReplaySubject(1);
        return this.identityEventsSubject.asObservable();
    }

    /** WEB-CORE-05: how long to wait after a 429 -- the server's Retry-After (60 s from professorx), else 5 s; at most 60 s. */
    static anon429DelayMs(err: any): number {
        const said = Number(err?.retryAfterSeconds);
        const wanted = Number.isFinite(said) && said > 0 ? said * 1000 : 5000;
        return Math.max(250, Math.min(wanted, LoginService.ANON_429_MAX_WAIT_MS));
    }

    private loadSessionInfo() {
        const apiDomain = this.apiConfig.getHttpsEndpoint();
        // This has to go though ws domain to get users actual IP and country
        const initializer$ = of({} as any); // location.href.indexOf('product') > -1 ? of({} as any) : this.windowService.userClicked;
        return initializer$.pipe(
            first(),
            mergeMap(() => {
                return this.lightHttp.post<SessionDetails>(
                    `${apiDomain}/users/session/settings`, {});
            })
        ).pipe(
            tap({
                next: (sessionDetailsObj) => {
                    const sessionDetails = plainToClass(SessionDetails, sessionDetailsObj);
                    this._userIpAddress = sessionDetails && (sessionDetails.ipv4 || sessionDetails.ip);
                    this._userCountry = sessionDetails && sessionDetails.country;
                    this._onSessionDetails.next(sessionDetails);
                }
            }),
            catchError(err => {
                this.scriptLoadService.loadAllScriptsIfNotLoaded(true).then(
                    () => {
                        this.uiContext.showErrorDialog({
                            title: 'Error', text: 'Failed to get session info. Please let us know about this issue @ support@xconvert.com. Please reload the page and try again.'
                        });
                    }
                ).catch(console.error);
                this.logger.error({ message: `Failed to get SessionDetails using ${apiDomain} domain.`, error: err });
                return of({} as SessionDetails);
            }),
        );
    }

    /**
     * Clear local session state without calling the server. Used when the backend
     * has already rejected our bearer (401/403) so a logout request is pointless.
     */
    clearLocalSession(): void {
        this.authService.unsetAuthToken();
        this.isUserLoggedIn = false;
        this.isUserAuthenticated = false;
        this.canGoOffline = false;
        this.offlineTaskService.setCanGoOfflineMode(false);
        this.updateUserDetails(null);
        this.emitSessionState(SessionState.ANONYMOUS);
    }

    logout(): Observable<any> {
        this.loginRequestDispatched = false;
        return concat(
            // withCredentials: send the __Secure-rt cookie so professorx REVOKES the session row,
            // AND let the clear-cookie Set-Cookie response apply (browser drops __Secure-rt/rt_present).
            // Without it this cross-origin POST neither revokes nor clears — the session survives and
            // the next bootstrap silently re-logs-in via the lingering rt_present cookie.
            this.lightHttp.post(this.apiConfig.getLogoutEndpoint(), {}, { withCredentials: true }).pipe(
                ResponsePipe,
            ).pipe(
                mergeMap((a) => {
                    this.authService.unsetAuthToken();
                    this.canGoOffline = false;
                    this.offlineTaskService.setCanGoOfflineMode(false);
                    this.isUserLoggedIn = false;
                    this.isUserAuthenticated = false;
                    return this.authenticate();
                }),
                tap(() => { this.updateUserDetails(null); }),
                mergeMap((res) => {
                    return this.login(null, null, null).pipe(
                        map(() => {
                            return res;
                        }),
                    );
                }),
            ),
        );
    }

    /**
     * If user already logged in and has a token, this method does nothing.
     */
    authenticate(): Observable<any> {
        if (this.authService.getToken()) {
            this.isUserAuthenticated = true;
            this.logger.trace7(`%cUser already has a valid token. Authentication skipped. Token state: ${!!this.authService.getToken()}`);
            return of({});
        }

        const request$ = this.lightHttp.post<AuthenticateResponse>(
            this.apiConfig.getAuthenticateEndpoint(),
            { authToken: this.authService.getToken(), },
            { withCredentials: true, },
        ).pipe(
            ResponsePipe,
            catchError(err => of(err)),
        );

        const subject = new ReplaySubject(1);
        if (!this.isUserAuthenticated && !this.loginRequestDispatched) {
            request$.subscribe({
                next: (result: AuthenticateResponse) => {
                    const token = result.bearer;
                    if (token) {
                        this.authService.setAuthToken(token);

                        this.storageService.store(StorageKeyPrefixeTypes.USER_SETTING_KEY_PREFIX,
                            SettingsServiceConstants.MOTIVE_STORAGE_KEY, result.motive);
                        // this.motivesService.setMotives(plainToClass(MotiveSettingsResponse, result.motive));

                        this.isUserAuthenticated = true;
                        this.logger.trace7(`%cUser authentication successful. Token updated!`);
                    } else {
                        this.logger.trace7(`%cSkipped respond from auth service. setAuthToken was already called. Token state: ${!!this.authService.getToken()} and PSToken: ${!!token}`);
                    }
                    subject.next(result);
                    subject.complete();
                },
                error: (err) => {
                    subject.error(err);
                },
            });
        }

        return subject.asObservable();
    }

    resetPassword(email: string, password: string, captchaToken: string, k: string): Observable<any> {
        return from(sha256(password || '')).pipe(
            mergeMap((passwordHash) => {
                return this.lightHttp.post(this.apiConfig.getResetPasswordEndpoint(), {
                    email,
                    password: passwordHash,
                    token: captchaToken,
                    k,
                },
                    {
                        withCredentials: true,
                    },
                ).pipe(
                    ResponsePipe,
                );
            })
        )
    }

    verifyAccount(k: string): Observable<any> {
        return this.lightHttp.post(
            this.apiConfig.getAccountVerifyEndpoint(),
            {
                k,
            },
            {
                withCredentials: true,
            },
        ).pipe(
            ResponsePipe,
        );
    }

    private getDefaultHeaders(): { location?: string } {
        return {
            ...(BrowserUtils.hasWindow() ? { 'location': window.location.href } : undefined),
        }
    }

    /**
     * F3r (review 7): the sign-in notice's Retry. Waits out what is left of the server's Retry-After (the toast says how
     * long), then sends ONE anonymous login -- no 429 wait loop of its own. A 429 again fails with the same
     * `IdentityUnavailableError` (and a fresh `retryAt`); the caller shows the notice again, never a dialog.
     */
    retryIdentityOnce(now: number = Date.now()): Observable<LoginResponse> {
        const waitMs = Math.max(0, Math.min((this.identityFailure?.retryAt ?? 0) - now, LoginService.ANON_429_MAX_WAIT_MS));
        if (waitMs > 0) this.uiContext.warnToast('Too many sign-ins from your network', `Retrying in ${Math.ceil(waitMs / 1000)} s…`, false, false);
        return timer(waitMs).pipe(mergeMap(() => this.login(null, null, null, { singleAttempt: true })));
    }

    login(email: string, password: string, captchaToken: string, options: { singleAttempt?: boolean } = {}): Observable<LoginResponse> {
        // Dedup anonymous probes (null/null/null) only. A real form login from the user
        // must never be merged with a concurrent anonymous probe.
        const isAnonymousProbe = !email && !password && !captchaToken;
        // F3r: `retryIdentityOnce` -- this probe does not wait out a 429 itself (it already waited Retry-After).
        const singleAttempt = options.singleAttempt === true;
        if (isAnonymousProbe && this.inFlightLogin$) {
            return this.inFlightLogin$;
        }
        this.loginRequestDispatched = true;
        // WEB-CORE-02: whether this probe runs WITHOUT an identity (only then is its 429 the user's problem).
        const heldNoBearer = !this.authService.getToken();
        const response$ = from(sha256(password)).pipe(
            mergeMap((passwordHash) => {
                return this.lightHttp.post(this.apiConfig.getLoginEndpoint(), {
                    email,
                    password: passwordHash,
                    token: captchaToken,
                    ...this.getDefaultHeaders(),
                },
                    {
                        withCredentials: true,
                    },
                ).pipe(
                    retry({
                        count: Math.max(2, LoginService.ANON_429_TRIES - 1),
                        delay: (err, n) => {
                            // Only retry on network-level failures (status 0 / no response).
                            // 4xx (bad credentials, consumed captcha) and 5xx must not auto-retry:
                            // captcha tokens are single-use, and login isn't idempotent.
                            const status = (err && err.status) || 0;
                            if (status === 0 && n <= 2) {
                                return timer(3000);
                            }
                            // F3g / WEB-CORE-05: the ANONYMOUS login with NO bearer held waits out a 429 (the IP's /auth/*
                            // limit) as long as the server says (bounded), once, and tries again. With a bearer held it does
                            // not (WEB-CORE-02): the identity is already there, the re-check simply failed.
                            if (status === 429 && isAnonymousProbe && heldNoBearer && !singleAttempt && n < LoginService.ANON_429_TRIES) {
                                const waitMs = LoginService.anon429DelayMs(err);
                                if (!this.identityRetryNoticed) {
                                    this.identityRetryNoticed = true;
                                    this.uiContext.warnToast('Too many sign-ins from your network', `Retrying in ${Math.ceil(waitMs / 1000)} s…`, false, false);
                                }
                                return timer(waitMs);
                            }
                            throw err;
                        },
                    }),
                    catchError((err) => {
                        // F3g: the anonymous login with NO bearer gave up on 429 -- one clear error, and identity-bound calls
                        // fail with it (`identityWait`) instead of each 404-ing without a word. With a bearer held it is the
                        // pre-F3g transient: the original error, nothing fatal (WEB-CORE-02).
                        if (isAnonymousProbe && heldNoBearer && ((err && err.status) || 0) === 429) {
                            // The boot path's notice (`_app.tsx` -> `noticeIdentityUnavailable`, non-blocking, Retry = one login after
                            // Retry-After) says it: this error's name and message are the words -- one message, never a dialog (F3r).
                            this.identityFailure = new IdentityUnavailableError(undefined, Date.now() + LoginService.anon429DelayMs(err));
                            this.identityEventsSubject?.next(this.identityFailure);
                            return throwError(() => this.identityFailure);
                        }
                        // WEB-CORE-03: every other anonymous-login failure is news for the calls waiting on a first identity too.
                        if (isAnonymousProbe && heldNoBearer) this.identityEventsSubject?.next(err ?? new Error('login failed'));
                        return throwError(() => err);
                    }),
                    tap(() => { this.identityFailure = null; this.identityRetryNoticed = false; }),
                    ResponsePipe,
                    mergeMap((res: LoginResponse) => {
                        if (res.bearer) {
                            this.authService.setAuthToken(res.bearer);
                        }
                        if (res.registered) {
                            this.offlineTaskService.setCanGoOfflineMode(res.canGoOffline);
                            this.isUserLoggedIn = true;
                            this.canGoOffline = res.canGoOffline;
                            this.emitSessionState(SessionState.LOGGED_IN);
                        } else {
                            this.offlineTaskService.setCanGoOfflineMode(!!res.canGoOffline);
                            this.canGoOffline = !!res.canGoOffline;
                            this.isUserLoggedIn = false;
                            this.emitSessionState(SessionState.ANONYMOUS);
                        }

                        this.storageService.store(
                            StorageKeyPrefixeTypes.USER_SETTING_KEY_PREFIX,
                            SettingsServiceConstants.MOTIVE_STORAGE_KEY, res.motive
                            );
                        // this.motivesService.setMotives(plainToClass(MotiveSettingsResponse, res.motive));

                        this.loginRequestDispatched = false;
                        if (res.registered) {
                            return this.getUserDetailsOnce$().pipe(
                                switchMap(
                                    (userDetails) => {
                                        this.updateUserDetails(plainToClass(AccountDetails, userDetails));
                                        return of(res);
                                    },
                                ),
                                catchError((err) => {
                                    // Backend said registered:true and gave us a bearer, but /user-details
                                    // rejected it (401/403) — the session is a lie. Self-heal: drop the
                                    // bearer and user-details so the user lands in anonymous state instead
                                    // of a 'logged in but no account' limbo.
                                    const status = (err && (err as any).status) || 0;
                                    if (status === 401 || status === 403) {
                                        this.logger.info({
                                            message: '/user-details rejected bearer after login; clearing session',
                                            data: { status },
                                        });
                                        this.authService.unsetAuthToken();
                                        this.isUserLoggedIn = false;
                                        this.canGoOffline = false;
                                        this.offlineTaskService.setCanGoOfflineMode(false);
                                        this.updateUserDetails(null);
                                        return of({ ...res, registered: false, bearer: null, error: true });
                                    }
                                    throw err;
                                }),
                            );
                        } else {
                            try {
                                this.updateUserDetails(null);
                            } catch (e) {
                                //
                            }
                        }
                        return of(res);
                    }),
                    mergeMap((res) => {
                        return this.loadSessionInfo().pipe(
                            map(() => {
                                return res;
                            }),
                        );
                    }),
                );
            })
        );

        const shared$ = response$.pipe(
            finalize(() => {
                if (this.inFlightLogin$ === shared$) {
                    this.inFlightLogin$ = null;
                }
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
        );
        if (isAnonymousProbe) {
            this.inFlightLogin$ = shared$;
        }
        return shared$;
    }

    private updateUserDetails(userDetails: AccountDetails) {
        this._userDetails = userDetails;
        this._onUserDetailsChanged.next(userDetails);
    }

    register(email: string, password: string, captchaToken: string): Observable<LoginResponse> {
        return from(sha256(password)).pipe(
            mergeMap((passwordHash) => {
                return this.lightHttp.post<LoginResponse>(this.apiConfig.getRegisterEndpoint(), {
                    email,
                    password: passwordHash,
                    token: captchaToken,
                    // withCredentials so the browser STORES the __Secure-rt + rt_present
                    // cookies the backend sets on successful registration (flag-on session
                    // issuance). Without it the cross-origin Set-Cookie is dropped and a
                    // freshly-registered user has a 30-min access token but no refresh cookie
                    // → silently logged out at expiry until they do a full login. Mirrors login().
                }, { withCredentials: true }).pipe(
                    ResponsePipe,
                    mergeMap((res: LoginResponse) => {
                        // Backend issues a session bearer on successful registration so the
                        // frontend no longer needs to call /auth/login with the same (now-consumed)
                        // reCAPTCHA token. Apply the same post-login state transitions here.
                        if (res.bearer) {
                            this.authService.setAuthToken(res.bearer);
                        }
                        if (res.registered) {
                            this.offlineTaskService.setCanGoOfflineMode(res.canGoOffline);
                            this.isUserLoggedIn = true;
                            this.canGoOffline = res.canGoOffline;
                            return this.getUserDetailsOnce$().pipe(
                                switchMap((userDetails) => {
                                    this.updateUserDetails(plainToClass(AccountDetails, userDetails));
                                    return of(res);
                                }),
                                catchError((err) => {
                                    const status = (err && (err as any).status) || 0;
                                    if (status === 401 || status === 403) {
                                        this.clearLocalSession();
                                        return of({ ...res, registered: false, bearer: null, error: true });
                                    }
                                    throw err;
                                }),
                            );
                        }
                        return of(res);
                    }),
                );
            }),
        );
    }

    validateEmail(email: string): Observable<ErrorResponse> {
        return this.lightHttp.post(this.apiConfig.getEmailValidateEndpoint(), {
            email,
        }).pipe(
            ResponsePipe,
        );
    }

    getUserDetailsOnce$(): Observable<AccountDetails> {
        return this.lightHttp.post(this.apiConfig.getUserDetailsEndpoint(), {}).pipe(
            ResponsePipe,
            map(resp => plainToClass(AccountDetails, resp))
        );
    }

    recover(email: string, captchaToken: string) {
        return this.lightHttp.post(this.apiConfig.getRecoverPasswordEndpoint(), {
            email,
            token: captchaToken,
        }).pipe(
            ResponsePipe,
        );
    }

    authorizeFileProcessRequest() {
        return this.lightHttp.post(this.apiConfig.getProcessAuthEndpoint(), {}).pipe(
            ResponsePipe,
        );
    }
}
